/**
 * ApplyReceiptCxP.js — Aplicar un pago a proveedor SIN APLICAR.
 *
 * Espejo de ApplyReceipt (CxC): el recibo ya está importado (monto, banco,
 * fecha, método fijos). Aquí se elige el proveedor y se distribuye el saldo
 * sin aplicar entre sus facturas pendientes.
 *
 * Modelo de moneda CxP: a diferencia de CxC (abonos en la moneda de la factura),
 * en CxP el `monto` de cada detalle se interpreta en la MONEDA DEL RECIBO
 * (registrar_pago(monto, moneda_pago=recibo.moneda_pago)). Por eso todo el
 * presupuesto, los abonos y el restante se manejan en una sola moneda: la del
 * recibo. `costInRecibo` convierte el saldo de cada factura a esa moneda.
 *
 * Funcionalidad equivalente a CxC:
 *   · Saldos a favor (CxP con saldo < 0): se muestran, ordenados primero, y al
 *     marcarlos su abono es NEGATIVO (crédito que se suma al presupuesto).
 *   · Pagos parciales: el usuario puede abonar menos que el saldo.
 *   · Excedente: si se abona más que el saldo de una factura, el sobrante deja
 *     esa CuentaXPagar en negativo = saldo a favor del proveedor.
 *   · Pagos cruzados (USD↔CRC) vía costInRecibo + tratar_como_diferencial.
 *   · Fusionar saldos a favor (≥2 marcados, mismo proveedor y moneda).
 */
import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../receipts/styles/addReceipt.module.css";
import PendingReceiptsTable from "../../shared/PendingReceiptsTable";
import { usePendingReceiptsCxP } from "../hooks/useReceiptCxP";
import { useTipoCambio } from "../../services/useTipoCambio";
import AcumularSaldosDialogCxP from "../components/AcumularSaldosDialogCxP";
import { SearchSelect } from "../../../../shared/components/SearchSelect";

const r2  = (n) => Math.round(Number(n) * 100) / 100;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ApplyReceiptCxP() {
  const { t } = useTranslation();
  const { reciboId } = useParams();
  const navigate = useNavigate();

  const [recibo, setRecibo]       = useState(null);
  const [loadingR, setLoadingR]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [acumulando, setAcumulando]   = useState(false);
  const [acumularMsg, setAcumularMsg] = useState(null);  // {ok, text}
  const [favorMsg, setFavorMsg]       = useState("");
  const [fechaPago, setFechaPago]     = useState("");
  const submittingRef = useRef(false);

  // Proveedor + facturas
  const [cuentasProveedor, setCuentasProveedor] = useState([]);
  const [selectedProveedor, setSelectedProveedor] = useState(0);
  // facturasData: [{ ...cxp, abono, favor, checked }] — abono en moneda del recibo
  const [facturasData, setFacturasData] = useState([]);
  const [loadingFac, setLoadingFac]   = useState(false);
  const [facKey, setFacKey]           = useState(0);   // fuerza recarga de facturas
  const [tratarDif, setTratarDif]     = useState(false);
  const [fusionOpen, setFusionOpen]   = useState(false);

  // Cola de pendientes
  const [pendingKey, setPendingKey] = useState(0);
  const { pending } = usePendingReceiptsCxP(pendingKey);

  // TC de respaldo (solo se usa para recibos USD con TC mal guardado)
  const { tipoCambio: tcBCCR } = useTipoCambio();

  // ── Cargar recibo ─────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingR(true);
    setRecibo(null);
    submittingRef.current = false;
    setSubmitting(false);
    // Reset del formulario al cambiar de recibo: evita arrastrar el proveedor,
    // las facturas marcadas y los abonos del recibo anterior al pasar al
    // "siguiente" (lo que confundía y podía generar registros duplicados).
    setSelectedProveedor(0);
    setFacturasData([]);
    setSubmitError("");
    setFavorMsg("");
    setTratarDif(false);
    setAcumularMsg(null);
    setFusionOpen(false);
    apiFetch(`/api/finance/reciboCxP/${reciboId}/`)
      .then((r) => r.json())
      .then((data) => {
        setRecibo(data);
        setFechaPago(data.fecha_pago ?? "");
        // Si el recibo se importó con un proveedor, preseleccionarlo para que el
        // usuario solo tenga que distribuir el monto (carga sus facturas pendientes).
        if (data.cuenta_proveedor) setSelectedProveedor(Number(data.cuenta_proveedor));
        setLoadingR(false);
      })
      .catch(() => setLoadingR(false));
  }, [reciboId]);

  // ── Cargar proveedores ────────────────────────────────────────────────
  useEffect(() => {
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&excluir_a_favor=1&bajo_relacion=proveedores&page_size=500")
      .then((r) => r.json())
      .then((d) => setCuentasProveedor(Array.isArray(d) ? d : (d.results ?? [])))
      .catch(() => {});
  }, []);

  const reciboMoneda = (recibo?.moneda_pago || recibo?.cuenta_bancaria?.moneda || "CRC").toUpperCase();
  // TC del recibo: si se guardó con TC≤1 (bug previo) y es USD, usar TC de BCCR
  const storedTC = Number(recibo?.tipo_cambio || 0);
  const reciboTC = reciboMoneda === "USD" && storedTC <= 1
    ? (Number(tcBCCR?.tipo_compra || 1) || 1)
    : (storedTC > 0 ? storedTC : 1);

  // Saldo sin aplicar = monto − suma de detalles ya aplicados (moneda del recibo)
  const saldoSinAplicar = useMemo(() => {
    if (!recibo) return 0;
    const aplicado = (recibo.detalles ?? []).reduce((s, d) => s + Number(d.monto || 0), 0);
    return r2(Number(recibo.monto || 0) - aplicado);
  }, [recibo]);

  // Costo (en moneda del recibo) para saldar el saldo de una factura.
  // Para saldos a favor (saldo < 0) devuelve un valor NEGATIVO = crédito.
  function costInRecibo(f) {
    const facMon = (f.moneda || "CRC").toUpperCase();
    if (facMon === reciboMoneda) return Number(f.saldo);
    if (reciboMoneda === "CRC") {
      // Factura USD → CRC. saldo_colones se piso a 0 en el backend, así que para
      // un saldo a favor (saldo < 0) se usa el TC de la factura para el crédito.
      return Number(f.saldo) < 0
        ? Number(f.saldo) * (Number(f.tipo_cambio) || 1)
        : Number(f.saldo_colones || 0);
    }
    return Number(f.saldo) / reciboTC;                                 // recibo USD, factura CRC
  }

  // ── Cargar facturas del proveedor (incluye saldos a favor) ────────────
  useEffect(() => {
    if (!selectedProveedor) { setFacturasData([]); return; }
    setLoadingFac(true);
    apiFetch("/api/finance/cuentaXPagar/?page_size=9999")
      .then((r) => r.json())
      .then((d) => {
        const all = d.results ?? d ?? [];
        const mine = all
          .filter((cxp) =>
            cxp.factura_cxp?.cuenta_proveedor?.id === Number(selectedProveedor) &&
            r2(Number(cxp.saldo)) !== 0)
          .sort((a, b) => {
            // saldos a favor (negativos) primero, luego por fecha
            const na = Number(a.saldo) < 0, nb = Number(b.saldo) < 0;
            if (na && !nb) return -1;
            if (!na && nb) return 1;
            return new Date(a.fecha_factura) - new Date(b.fecha_factura) || a.id - b.id;
          })
          .map((cxp) => ({ ...cxp, abono: 0, favor: 0, checked: false, comision_bancaria: 0 }));
        setFacturasData(mine);
      })
      .finally(() => setLoadingFac(false));
  }, [selectedProveedor, facKey]);

  // ── Totales / restante (en moneda del recibo) ─────────────────────────
  // Los abonos negativos (créditos a favor seleccionados) AUMENTAN lo disponible.
  const sumAbonos = useMemo(
    () => r2(facturasData.reduce((s, f) => s + (f.checked ? Number(f.abono || 0) : 0), 0)),
    [facturasData]
  );
  const restante = r2(saldoSinAplicar - sumAbonos);

  // ── Marcar/desmarcar una factura ──────────────────────────────────────
  function toggleFactura(id) {
    setFavorMsg("");
    setFacturasData((prev) => {
      const target = prev.find((f) => f.id === id);
      if (!target) return prev;
      const cost = r2(costInRecibo(target));

      if (target.checked) {
        return prev.map((f) => f.id === id ? { ...f, abono: 0, favor: 0, checked: false, comision_bancaria: 0 } : f);
      }

      // Saldo a favor: marcar aplica el crédito completo (abono negativo)
      if (Number(target.saldo) < 0) {
        return prev.map((f) => f.id === id ? { ...f, abono: cost, favor: 0, checked: true } : f);
      }

      // Factura pendiente: abonar hasta lo disponible (sin pasarse del saldo)
      const usado = prev.reduce((s, f) => s + (f.checked ? Number(f.abono || 0) : 0), 0);
      const disponible = r2(saldoSinAplicar - usado);
      const abono = r2(Math.min(cost, Math.max(0, disponible)));
      return prev.map((f) => f.id === id
        ? { ...f, abono: abono > 0 ? abono : 0, favor: 0, checked: abono > 0 }
        : f);
    });
  }

  // ── Editar abono manualmente (moneda del recibo) ──────────────────────
  function updateAbono(id, rawValue) {
    const value  = r2(Number(rawValue) || 0);
    const target = facturasData.find((f) => f.id === id);
    // Excedente sobre una factura pendiente → saldo a favor (no se rechaza)
    let favor = 0, msg = "";
    if (target && Number(target.saldo) >= 0) {
      const cost = r2(costInRecibo(target));
      if (value > cost) {
        favor = r2(value - cost);
        msg = t("recibos.alertaFavor", {
          favor: `${reciboMoneda === "USD" ? "$" : "₡"}${fmt(favor)}`,
        });
      }
    }
    setFavorMsg(msg);
    setFacturasData((prev) => prev.map((f) =>
      f.id === id ? { ...f, abono: value, favor, checked: value !== 0 } : f));
  }

  // ── Comisión bancaria (pago con Tarjeta) — gasto adicional que sale del
  // banco junto con el abono, pero no reduce el saldo de la factura ni
  // consume presupuesto del recibo (no afecta `restante`).
  const esTarjeta = (recibo?.metodo_pago || "") === "TARJETA";
  function updateComision(id, rawValue) {
    const value = Math.max(0, r2(Number(rawValue) || 0));
    setFacturasData((prev) => prev.map((f) =>
      f.id === id ? { ...f, comision_bancaria: value } : f));
  }

  // ── Distribuir automáticamente ────────────────────────────────────────
  function distribute() {
    setFavorMsg("");
    // Crédito de saldos a favor ya marcados (abono negativo)
    const credito = facturasData.reduce(
      (s, f) => s + (Number(f.saldo) < 0 && f.checked ? -r2(costInRecibo(f)) : 0), 0);
    let rem = r2(saldoSinAplicar + credito);
    if (rem <= 0) { setFavorMsg(t("recibos.alertaSinMonto")); return; }

    const positivos = facturasData.filter((f) => Number(f.saldo) > 0);
    const lastPosId = positivos.length ? positivos[positivos.length - 1].id : null;
    let avisoFavor = 0;

    const next = facturasData.map((f) => {
      if (Number(f.saldo) < 0) {
        // saldo a favor: conservar selección actual
        return f.checked
          ? { ...f, abono: r2(costInRecibo(f)), favor: 0, checked: true }
          : { ...f, abono: 0, favor: 0, checked: false };
      }
      if (rem <= 0) return { ...f, abono: 0, favor: 0, checked: false };
      const cost = r2(costInRecibo(f));
      let abono, favor = 0;
      if (f.id === lastPosId) {
        abono = r2(rem);                       // la última absorbe el resto
        if (abono > cost) { favor = r2(abono - cost); avisoFavor = favor; }
        rem = 0;
      } else {
        abono = r2(Math.min(cost, rem));
        rem = r2(rem - abono);
      }
      return { ...f, abono, favor, checked: abono > 0 };
    });
    setFacturasData(next);
    if (avisoFavor > 0) {
      setFavorMsg(t("recibos.alertaFavor", {
        favor: `${reciboMoneda === "USD" ? "$" : "₡"}${fmt(avisoFavor)}`,
      }));
    }
  }

  const hayCruzado = useMemo(
    () => facturasData.some((f) => f.checked && (f.moneda || "CRC").toUpperCase() !== reciboMoneda),
    [facturasData, reciboMoneda]
  );

  // Saldos a favor marcados (para fusión)
  const negativasChecked = facturasData.filter((f) => f.checked && Number(f.saldo) < 0);
  const puedeFusionar = negativasChecked.length >= 2;

  const siguientes = pending.filter((p) => p.id !== Number(reciboId));

  // ── Guardar aplicación ────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitError("");
    if (!selectedProveedor) { setSubmitError(t("cxp.proveedorReqMsg")); return; }
    if (Math.abs(restante) > 0.01) { setSubmitError(t("recibos.restanteError")); return; }
    const detalles = facturasData
      .filter((f) => f.checked && Number(f.abono) !== 0)
      .map((f) => ({
        factura_cxp: f.factura_cxp.id,
        monto: r2(Number(f.abono)),
        tratar_como_diferencial: ((f.moneda || "CRC").toUpperCase() !== reciboMoneda) && tratarDif,
        comision_bancaria: esTarjeta ? r2(Number(f.comision_bancaria || 0)) : 0,
      }));
    if (detalles.length === 0) { setSubmitError(t("cxp.seleccionaFacturaCxP")); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${reciboId}/aplicar/`, {
        method: "POST",
        body: JSON.stringify({ detalles, fecha_pago: fechaPago || recibo.fecha_pago }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : t("cxp.errorGuardar"));
      }
      setPendingKey((k) => k + 1);
      const sig = pending.filter((p) => p.id !== Number(reciboId));
      navigate(sig.length > 0 ? `/finanzas/cxp/recibos/${sig[0].id}/aplicar` : "/finanzas/cxp/recibos");
    } catch (err) {
      setSubmitError(err.message);
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  // ── Acumular el saldo sobrante como saldo a favor del proveedor ────────
  async function handleAcumular() {
    if (acumulando || submittingRef.current) return;
    if (!selectedProveedor) { setAcumularMsg({ ok: false, text: t("cxp.proveedorReqMsg") }); return; }
    setAcumularMsg(null);
    setAcumulando(true);
    submittingRef.current = true;
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${reciboId}/acumular_saldo/`, {
        method: "POST",
        body: JSON.stringify({ proveedor_id: Number(selectedProveedor), fecha_pago: fechaPago || recibo.fecha_pago }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? JSON.stringify(data));
      setAcumularMsg({ ok: true, text: t("cxp.saldoAcumuladoOk", { asiento: data.asiento ?? "—" }) });
      setPendingKey((k) => k + 1);
      setTimeout(() => {
        const sig = pending.filter((p) => p.id !== Number(reciboId));
        navigate(sig.length > 0 ? `/finanzas/cxp/recibos/${sig[0].id}/aplicar` : "/finanzas/cxp/recibos");
      }, 1500);
    } catch (err) {
      setAcumularMsg({ ok: false, text: err.message || t("cxp.errorGuardar") });
      submittingRef.current = false;
    } finally {
      setAcumulando(false);
    }
  }

  if (loadingR) return <p style={{ padding: 20, color: "var(--text-secondary)" }}>{t("common.cargando")}…</p>;
  if (!recibo)  return <p style={{ padding: 20, color: "#f87171" }}>{t("recibos.noEncontrado")}</p>;

  const sinFacturasAbiertas = selectedProveedor && facturasData.length === 0;
  const simbolo = reciboMoneda === "USD" ? "$" : "₡";

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/"><span className={styles.inactive}>{t("common.inicio")} /</span></Link>
        <Link to="/finanzas/cxp"><span className={styles.inactive}> {t("cxp.titulo")} /</span></Link>
        <Link to="/finanzas/cxp/recibos"><span className={styles.inactive}> {t("cxp.recibos")} /</span></Link>
        <span className={styles.active}> {t("recibos.aplicar")} {recibo.numero_recibo}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("recibos.aplicar")} — {recibo.numero_recibo}</h2>

      {/* Banner recibo pendiente */}
      <div style={{ background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.35)",
        borderLeft:"4px solid #f97316", borderRadius:6, padding:"10px 16px", marginBottom:12,
        fontSize:"0.87rem", color:"#fdba74" }}>
        <strong>⚠️ {t("cxp.pagoSinAplicarBanner")}</strong> {t("cxp.pagoSinAplicarDesc")}
        {siguientes.length > 0 && (
          <span style={{ marginLeft:8, color:"#fb923c" }}>
            · {t("recibos.quedanMas", { n: siguientes.length })}
          </span>
        )}
      </div>

      {/* Cola de pendientes */}
      <PendingReceiptsTable
        pendientes={pending}
        currentId={Number(reciboId)}
        pageSize={5}
        applyBasePath="/finanzas/cxp/recibos"
        entityLabel={t("cxp.proveedor")}
        title={t("cxp.colaPendientes", { n: pending.length })}
      />

      {submitError && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"10px 16px", marginBottom:12,
          color:"#f87171", fontSize:"0.86rem", display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{submitError}
        </div>
      )}

      <div className={styles["receipt-container"]}>
        {/* ── Izquierda: datos del recibo (read-only) ── */}
        <div className={styles["receipt-details"]}>
          <div className={styles["receipt-details-header"]}>
            <h1><i className={`fa-solid fa-money-bill-transfer ${styles["icon-receipt-details"]}`}/>{t("cxp.detallesPago")}</h1>
          </div>
          <div className={styles["receipt-details-body"]} style={{ marginTop:14 }}>
            {/* Proveedor (editable: el pago sin aplicar no tiene proveedor aún) */}
            <div>
              <label className="form-label">{t("cxp.proveedor")} <span style={{ color:"#f87171" }}>*</span></label>
              <SearchSelect
                icon="fa-building"
                options={cuentasProveedor.map((c) => ({ id: c.id, primary: c.codigo, secondary: c.nombre }))}
                value={selectedProveedor || null}
                onChange={(id) => setSelectedProveedor(Number(id) || 0)}
                placeholder={t("cxp.seleccionaProveedor")}
              />
            </div>

            {/* Monto sin aplicar (read-only) */}
            <div className="row" style={{ marginTop:12 }}>
              <div className="form-group">
                <label className="form-label">
                  {t("recibos.sinAplicar")}{" "}
                  <span style={{ color: reciboMoneda === "USD" ? "#60a5fa" : "#4ade80", fontWeight:700, fontSize:"0.82rem" }}>
                    ({reciboMoneda === "USD" ? "$ USD" : "₡ CRC"})
                  </span>
                </label>
                <input className="form-control" type="text" disabled
                  value={`${simbolo}${fmt(saldoSinAplicar)}`}
                  style={{ fontWeight:600 }}/>
                <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3 }}>
                  {t("recibos.montoOriginal")}: {simbolo}{fmt(recibo.monto)}
                </div>
              </div>
            </div>

            {/* Fecha + Método (read-only) */}
            <div className="row">
              <div className="col">
                <div className="form-group">
                  <label className="form-label">{t("cxp.fechaPago")}</label>
                  <input className="form-control" type="date" value={fechaPago} disabled/>
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <label className="form-label">{t("cxp.medioPago")}</label>
                  <input className="form-control" type="text" disabled
                    value={t(`recibos.${(recibo.metodo_pago || "").toLowerCase()}`) || recibo.metodo_pago}/>
                </div>
              </div>
            </div>

            {/* Banco + documento (read-only) */}
            <div className="row">
              <div className="form-group">
                <label className="form-label">{t("cxp.cuentaBancaria")}</label>
                <input className="form-control" type="text" disabled
                  value={recibo.cuenta_bancaria ? `${recibo.cuenta_bancaria.banco} — ${recibo.cuenta_bancaria.numero_cuenta}` : "—"}/>
              </div>
            </div>
            <div className="row">
              <div className="form-group">
                <label className="form-label">{t("cxp.referenciaBancaria")}</label>
                <input className="form-control" type="text" disabled
                  value={recibo.documento || "—"}/>
              </div>
            </div>

            {/* Diferencial si hay pago cruzado */}
            {hayCruzado && (
              <div style={{ background:"rgba(251,146,60,0.08)", border:"1px solid rgba(251,146,60,0.3)",
                borderRadius:7, padding:"10px 14px", fontSize:"0.8rem" }}>
                <div style={{ color:"#fb923c", fontWeight:600, marginBottom:6 }}>
                  <i className="fa-solid fa-arrow-right-arrow-left" style={{ marginRight:6 }}/>{t("cxp.pagoCruzado")}
                </div>
                <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", color:"var(--text-secondary)" }}>
                  <input type="checkbox" checked={tratarDif} onChange={(e) => setTratarDif(e.target.checked)}
                    style={{ accentColor:"#f97316", width:14, height:14 }}/>
                  <span style={{ fontSize:"0.78rem" }}>{t("cxp.tratarDiferencial")}</span>
                </label>
              </div>
            )}

            {/* Info asiento */}
            <div style={{ padding:"10px 14px", background:"rgba(96,165,250,0.06)",
              border:"1px solid rgba(96,165,250,0.2)", borderRadius:6, fontSize:"0.8rem", color:"#93c5fd" }}>
              <i className="fa-solid fa-book-open" style={{ marginRight:6 }}/>
              {t("cxp.asientoInfoPrefix")} <strong>{t("asientos.debe").toUpperCase()}</strong> {t("cxp.proveedorDefault")}
              {" · "}<strong>{t("asientos.haber").toUpperCase()}</strong> {t("cxp.bancoLabel")}
            </div>
          </div>
        </div>

        {/* ── Derecha: facturas del proveedor ── */}
        <div className={styles["cxc-details"]}>
          <div className={styles["cxc-details-header"]}>
            <h1><i className={`fa-solid fa-file-invoice ${styles["icon-cxc-details"]}`}/>{t("cxp.facturasPendientes")}</h1>
          </div>
          <div className={styles["receipt-details-body"]}>
            {!selectedProveedor ? (
              <div style={{ padding:"40px 16px", textAlign:"center", color:"var(--text-muted)" }}>
                <i className="fa-solid fa-building" style={{ fontSize:"2rem", display:"block", marginBottom:12 }}/>
                {t("cxp.sinProveedor")}
              </div>
            ) : loadingFac ? (
              <div style={{ padding:40, textAlign:"center" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ color:"#f97316", fontSize:"1.3rem" }}/>
              </div>
            ) : facturasData.length === 0 ? (
              <div style={{ padding:32, textAlign:"center", color:"var(--text-muted)", fontSize:"0.87rem" }}>
                <i className="fa-solid fa-check-circle" style={{ color:"#4ade80", fontSize:"1.4rem", display:"block", marginBottom:8 }}/>
                {t("cxp.sinFacturasCxP")}
                <div style={{ fontSize:"0.8rem", marginTop:8 }}>{t("cxp.acumularHint")}</div>
              </div>
            ) : (
              <>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <div style={{ display:"flex", gap:8 }}>
                    <button type="button" onClick={distribute}
                      style={{ padding:"6px 12px", background:"rgba(249,115,22,0.12)", border:"1px solid rgba(249,115,22,0.4)",
                        borderRadius:6, color:"#f97316", fontWeight:600, fontSize:"0.8rem", cursor:"pointer" }}>
                      <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight:6 }}/>{t("recibos.distribuir")}
                    </button>
                    {puedeFusionar && (
                      <button type="button" onClick={() => setFusionOpen(true)}
                        style={{ padding:"6px 12px", background:"rgba(74,222,128,0.12)", border:"1px solid rgba(74,222,128,0.4)",
                          borderRadius:6, color:"#4ade80", fontWeight:600, fontSize:"0.8rem", cursor:"pointer", whiteSpace:"nowrap" }}>
                        <i className="fa-solid fa-layer-group" style={{ marginRight:6 }}/>{t("recibos.acumularSaldos")}
                      </button>
                    )}
                  </div>
                  <span style={{ fontSize:"0.82rem", color: Math.abs(restante) < 0.01 ? "#4ade80" : "#f87171", fontWeight:600 }}>
                    {t("recibos.restante")}: {simbolo}{fmt(restante)}
                  </span>
                </div>

                {esTarjeta && (() => {
                  const totalComision = r2(facturasData.reduce(
                    (s, f) => s + (f.checked ? Number(f.comision_bancaria || 0) : 0), 0));
                  if (totalComision <= 0) return null;
                  return (
                    <div style={{ marginBottom:8, display:"flex", justifyContent:"space-between",
                      fontSize:"0.8rem", color:"#fcd34d" }}>
                      <span><i className="fa-solid fa-credit-card" style={{ marginRight:6 }}/>{t("cxp.comisionBancaria")}</span>
                      <span style={{ fontWeight:700 }}>{simbolo}{fmt(totalComision)}</span>
                    </div>
                  );
                })()}

                {favorMsg && (
                  <div style={{ marginBottom:8, padding:"7px 12px", borderRadius:6,
                    background:"rgba(251,146,60,0.1)", border:"1px solid rgba(251,146,60,0.3)",
                    color:"#fb923c", fontSize:"0.8rem" }}>
                    <i className="fa-solid fa-piggy-bank" style={{ marginRight:6 }}/>{favorMsg}
                  </div>
                )}

                <div style={{ width:"100%", overflowX:"auto", border:"1px solid var(--border-card)", borderRadius:8 }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", color:"var(--text-primary)" }}>
                    <thead>
                      <tr style={{ background:"var(--bg-card)", borderBottom:"1px solid var(--border-card)" }}>
                        <th style={{ width:34, padding:"10px 8px" }}></th>
                        <th style={{ padding:"10px 10px", textAlign:"left", color:"var(--text-secondary)", fontSize:"0.78rem" }}>{t("cxp.numFactura")}</th>
                        <th style={{ padding:"10px 10px", textAlign:"center", color:"var(--text-secondary)", fontSize:"0.78rem" }}>{t("common.moneda")}</th>
                        <th style={{ padding:"10px 10px", textAlign:"right", color:"var(--text-secondary)", fontSize:"0.78rem" }}>{t("cxp.saldo")}</th>
                        <th style={{ padding:"10px 10px", textAlign:"right", color:"var(--text-secondary)", fontSize:"0.78rem", width:"30%" }}>{t("recibos.abono")} ({simbolo})</th>
                        {esTarjeta && (
                          <th style={{ padding:"10px 10px", textAlign:"right", color:"#fcd34d", fontSize:"0.78rem", width:"20%" }}>{t("cxp.comisionBancaria")}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {facturasData.map((cxp) => {
                        const fac = cxp.factura_cxp;
                        const sel = cxp.checked;
                        const facMon = (cxp.moneda || "CRC").toUpperCase();
                        const esFavor = Number(cxp.saldo) < 0;
                        return (
                          <tr key={cxp.id} style={{ background: sel ? "rgba(249,115,22,0.08)" : "transparent",
                            borderBottom:"1px solid var(--border-card)" }}>
                            <td style={{ textAlign:"center", padding:"8px" }}>
                              <input type="checkbox" checked={sel} onChange={() => toggleFactura(cxp.id)}
                                style={{ accentColor: esFavor ? "#4ade80" : "#f97316" }}/>
                            </td>
                            <td style={{ padding:"8px 10px", fontFamily:"monospace", color:"#f97316", fontWeight:600, fontSize:"0.82rem", cursor:"pointer" }}
                              onClick={() => toggleFactura(cxp.id)}>
                              {fac?.numero_doc_proveedor || fac?.numero_factura || "—"}
                              {esFavor && (
                                <span style={{ marginLeft:6, padding:"1px 6px", borderRadius:4, fontSize:"0.65rem",
                                  fontWeight:700, background:"rgba(74,222,128,0.15)", color:"#4ade80" }}>
                                  {t("cxp.aFavor")}
                                </span>
                              )}
                            </td>
                            <td style={{ padding:"8px 10px", textAlign:"center" }}>
                              <span style={{ padding:"2px 7px", borderRadius:4, fontSize:"0.7rem", fontWeight:700,
                                background: facMon === "USD" ? "rgba(96,165,250,0.15)" : "rgba(74,222,128,0.12)",
                                color: facMon === "USD" ? "#60a5fa" : "#4ade80" }}>{facMon}</span>
                            </td>
                            <td style={{ padding:"8px 10px", textAlign:"right", fontFamily:"monospace",
                              color: esFavor ? "#4ade80" : "#facc15", fontWeight:600 }}>
                              {facMon === "USD" ? `$${fmt(cxp.saldo)}` : `₡${fmt(cxp.saldo)}`}
                              {/* Conversión a la moneda del recibo cuando la factura está en otra moneda */}
                              {facMon !== reciboMoneda && (
                                <div style={{ fontSize:"0.68rem", color:"var(--text-muted)", fontWeight:400, marginTop:1 }}>
                                  ≈ {reciboMoneda === "USD" ? "$" : "₡"}{fmt(Math.abs(costInRecibo(cxp)))}
                                </div>
                              )}
                            </td>
                            <td style={{ padding:"6px 10px", textAlign:"right" }}>
                              <input type="number" step="0.01" disabled={!sel || esFavor}
                                value={sel ? (cxp.abono ?? "") : ""}
                                onChange={(e) => updateAbono(cxp.id, e.target.value)}
                                style={{ width:"100%", textAlign:"right", padding:"5px 8px", borderRadius:5,
                                  border:"1px solid var(--border-card)", background: sel ? "var(--bg-input,#1c1c1c)" : "transparent",
                                  color: esFavor ? "#4ade80" : "var(--text-primary)" }}/>
                            </td>
                            {esTarjeta && (
                              <td style={{ padding:"6px 10px", textAlign:"right" }}>
                                <input type="number" min="0" step="0.01" disabled={!sel || esFavor}
                                  value={sel ? (cxp.comision_bancaria ?? "") : ""}
                                  onChange={(e) => updateComision(cxp.id, e.target.value)}
                                  placeholder="0.00"
                                  style={{ width:"100%", textAlign:"right", padding:"5px 8px", borderRadius:5,
                                    border:"1px solid rgba(250,204,21,0.35)",
                                    background: sel ? "rgba(250,204,21,0.06)" : "transparent",
                                    color:"#fcd34d" }}/>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Banner acumular */}
        {acumularMsg && (
          <div style={{ gridColumn:"1 / -1", margin:"0 0 8px", padding:"10px 16px", borderRadius:6,
            background: acumularMsg.ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)",
            border: `1px solid ${acumularMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
            borderLeft: `4px solid ${acumularMsg.ok ? "#4ade80" : "#f87171"}`,
            color: acumularMsg.ok ? "#4ade80" : "#f87171", fontSize:"0.87rem",
            display:"flex", alignItems:"center", gap:8 }}>
            <i className={`fa-solid ${acumularMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}/>{acumularMsg.text}
          </div>
        )}

        {/* ── Footer ── */}
        <div className={styles["receipt-footer"]}>
          <div className={styles.actions}>
            <Link to="/finanzas/cxp/recibos" className={`${styles.btn} ${styles["btn-clean-form"]}`} style={{ textDecoration:"none" }}>
              <i className="fa-solid fa-arrow-left"/> {t("recibos.volverSinGuardar")}
            </Link>

            {sinFacturasAbiertas && (
              <button type="button" onClick={handleAcumular} disabled={acumulando || submitting}
                style={{ padding:"9px 18px", background:"rgba(167,139,250,0.15)", border:"1px solid rgba(167,139,250,0.45)",
                  borderRadius:6, color:"#a78bfa", fontWeight:600, fontSize:14, cursor: acumulando ? "not-allowed" : "pointer",
                  display:"flex", alignItems:"center", gap:7, opacity: acumulando ? 0.7 : 1 }}>
                {acumulando
                  ? <><span className="spinner-border spinner-border-sm" role="status"/>&nbsp;{t("cxp.acumulando")}</>
                  : <><i className="fa-solid fa-piggy-bank"/> {t("cxp.acumularSaldoFavor")}</>}
              </button>
            )}

            <button className={`${styles.btn} ${styles["btn-save"]}`} onClick={handleSubmit} disabled={submitting || acumulando}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm" role="status"/>&nbsp;{t("cxp.guardando")}</>
                : <><i className="fa-solid fa-save"/> {siguientes.length > 0
                    ? t("recibos.guardarYSiguiente", { n: siguientes.length })
                    : t("recibos.guardarAplicacion")}</>}
            </button>
          </div>
        </div>
      </div>

      {fusionOpen && (
        <AcumularSaldosDialogCxP
          negativas={negativasChecked}
          onClose={() => setFusionOpen(false)}
          onDone={() => { setFusionOpen(false); setFacKey((k) => k + 1); }}
        />
      )}
    </>
  );
}
